import { FieldProps, FormikTouched } from 'formik';
import { useCallback } from 'react';
import {
  FormPageInputData,
  FormPageProperty,
  FormPageInputItem,
  valueOf,
} from 'shared';

type UsePropertyContainerHandlerPayload = {
  fieldProps: FieldProps<FormPageInputItem, FormPageInputData>;
  propertyId: FormPageProperty['propertyId'];
};

type UpdateFieldValue<T extends FormPageInputItem = FormPageInputItem> = (
  key: keyof T,
  value: valueOf<T>
) => void;

type UpdateFieldValueWithCallback<
  T extends FormPageInputItem = FormPageInputItem
> = (updateCallback: (currentPropertyValues: T) => T) => void;

type UpdateFieldTouched<T extends FormPageInputItem = FormPageInputItem> = (
  updatePropertyKey: keyof T
) => void;

type UsePropertyContainerHandlerReturnType<
  T extends FormPageInputItem = FormPageInputItem
> = {
  fieldTouched: FormikTouched<FormPageInputItem> | undefined;
  fieldErrorMessages: string[] | undefined;
  updateFieldValue: UpdateFieldValue<T>;
  updateFieldValueWithCallback: UpdateFieldValueWithCallback<T>;
  updateFieldTouched: UpdateFieldTouched<T>;
};

export const useFieldPropsHandler = <
  T extends FormPageInputItem = FormPageInputItem
>({
  fieldProps,
  propertyId,
}: UsePropertyContainerHandlerPayload): UsePropertyContainerHandlerReturnType<T> => {
  const { touched, setTouched, errors, setValues } = fieldProps.form;
  const fieldTouched = touched[propertyId];
  const fieldErrorMessages = errors[propertyId] as string[] | undefined;
  const updateFieldValue: UpdateFieldValue<T> = useCallback(
    (key, value) => {
      setValues((values) => ({
        ...values,
        [propertyId]: {
          ...values[propertyId],
          [key]: value,
        },
      }));
    },
    [setValues, propertyId]
  );
  const updateFieldValueWithCallback: UpdateFieldValueWithCallback<T> =
    useCallback(
      (updateCallback) => {
        setValues((values) => ({
          ...values,
          [propertyId]: {
            ...updateCallback({ ...values[propertyId] } as T),
          } as T,
        }));
      },
      [setValues, propertyId]
    );
  const updateFieldTouched: UpdateFieldTouched<T> = useCallback(
    (key) => {
      const updatedTouched = {
        ...touched,
        [propertyId]: {
          ...fieldTouched,
          [key]: true,
        },
      };
      setTouched(updatedTouched, false);
    },
    [touched, fieldTouched, setTouched, propertyId]
  );

  return {
    fieldTouched,
    fieldErrorMessages,
    updateFieldValue,
    updateFieldValueWithCallback,
    updateFieldTouched,
  };
};
